import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { userAPI, bookingAPI } from '../services/api';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import Icon from '../components/Icon';

function MyBikesPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const navigate = useNavigate();
  const [bikes, setBikes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showStolenModal, setShowStolenModal] = useState(null);
  const [showAddBikeModal, setShowAddBikeModal] = useState(false);
  const [dismissedServiceReminders, setDismissedServiceReminders] = useState(() => {
    const saved = localStorage.getItem('dismissedServiceReminders');
    return saved ? JSON.parse(saved) : [];
  });
  const [showServiceReminderModal, setShowServiceReminderModal] = useState(null);
  const [bikeForm, setBikeForm] = useState({
    vin: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    description: ''
  });
  const [addingBike, setAddingBike] = useState(false);
  const [addBikeError, setAddBikeError] = useState('');

  useEffect(() => {
    fetchBikes();
    fetchBookings();
  }, []);

  const fetchBikes = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getMyBikes();
      const bikesData = response.data.bikes || [];
      setBikes(bikesData);
      
      // Check for bikes that need service reminder (>12 months)
      const today = new Date();
      for (const bike of bikesData) {
        if (bike.services && bike.services.length > 0) {
          // Get most recent completed service
          const completedServices = bike.services
            .filter(s => s.status === 'completed' && s.service_date)
            .sort((a, b) => new Date(b.service_date) - new Date(a.service_date));
          
          if (completedServices.length > 0) {
            const lastServiceDate = new Date(completedServices[0].service_date);
            const monthsSinceService = (today - lastServiceDate) / (1000 * 60 * 60 * 24 * 30);
            
            if (monthsSinceService > 12 && !dismissedServiceReminders.includes(bike.id)) {
              setShowServiceReminderModal(bike);
              break; // Show one at a time
            }
          }
        }
      }
    } catch (err) {
      setError('Failed to load bikes');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await bookingAPI.getUserBookings().catch(() => ({ data: { bookings: [] } }));
      const allBookings = response.data.bookings || [];
      const today = new Date().toISOString().split('T')[0];
      const upcoming = allBookings.filter(b => 
        b.booking_date >= today && (b.status === 'pending' || b.status === 'confirmed')
      );
      setBookings(upcoming);
    } catch (err) {
      // Silently fail, bookings are optional
    }
  };

  const getServiceStatusColor = (bike) => {
    if (!bike.services || bike.services.length === 0) {
      return null; // No services yet
    }
    
    const completedServices = bike.services
      .filter(s => s.status === 'completed' && s.service_date)
      .sort((a, b) => new Date(b.service_date) - new Date(a.service_date));
    
    if (completedServices.length === 0) {
      return null; // No completed services
    }
    
    const lastServiceDate = new Date(completedServices[0].service_date);
    const today = new Date();
    const monthsSinceService = (today - lastServiceDate) / (1000 * 60 * 60 * 24 * 30);
    
    if (monthsSinceService < 6) {
      return '#22c55e'; // Green
    } else if (monthsSinceService < 12) {
      return '#f97316'; // Orange
    } else {
      return '#ef4444'; // Red
    }
  };

  const getBikeBookings = (bikeId) => {
    return bookings.filter(b => b.bike_id === bikeId);
  };

  const handleDismissServiceReminder = (bikeId) => {
    const updated = [...dismissedServiceReminders, bikeId];
    setDismissedServiceReminders(updated);
    localStorage.setItem('dismissedServiceReminders', JSON.stringify(updated));
    setShowServiceReminderModal(null);
    
    // Check for next bike that needs reminder
    const today = new Date();
    for (const bike of bikes) {
      if (bike.id === bikeId) continue;
      if (bike.services && bike.services.length > 0) {
        const completedServices = bike.services
          .filter(s => s.status === 'completed' && s.service_date)
          .sort((a, b) => new Date(b.service_date) - new Date(a.service_date));
        
        if (completedServices.length > 0) {
          const lastServiceDate = new Date(completedServices[0].service_date);
          const monthsSinceService = (today - lastServiceDate) / (1000 * 60 * 60 * 24 * 30);
          
          if (monthsSinceService > 12 && !updated.includes(bike.id)) {
            setShowServiceReminderModal(bike);
            break;
          }
        }
      }
    }
  };

  const handleReportStolen = async (bikeId) => {
    try {
      await userAPI.reportBikeStolen(bikeId);
      setSuccess('Bike reported as stolen. Service shops will be alerted.');
      setShowStolenModal(null);
      fetchBikes();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    }
  };

  const handleReportFound = async (bikeId) => {
    try {
      await userAPI.reportBikeFound(bikeId);
      setSuccess('Bike marked as found. Stolen status removed.');
      fetchBikes();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    }
  };

  const handleAddBike = async (e) => {
    e.preventDefault();
    setAddBikeError('');
    setAddingBike(true);

    try {
      await userAPI.createBike(bikeForm);
      setSuccess('Bike added successfully!');
      setShowAddBikeModal(false);
      setBikeForm({ vin: '', brand: '', model: '', year: new Date().getFullYear(), description: '' });
      fetchBikes();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setAddBikeError(err.response?.data?.error || 'Failed to add bike');
    } finally {
      setAddingBike(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} pageTitle={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="bikePassports" color="primary" darkMode={darkMode} size={20} />
          My Bike Passports
        </span>
      } darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="bikePassports" color="primary" darkMode={darkMode} size={20} />
          My Bike Passports
        </span>
      }
      pageSubtitle={`${bikes.length} bike ${bikes.length === 1 ? 'passport' : 'passports'} registered`}
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
    >
      {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
      {success && <div className="success" style={{ marginBottom: '20px' }}>{success}</div>}

      {/* Add Bike Button */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => {
            setShowAddBikeModal(true);
            setAddBikeError('');
          }}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Icon name="upload" color="primary" darkMode={darkMode} size={16} />
          Add new bike
        </button>
      </div>

      {/* Bikes Grid */}
      {bikes.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'var(--bg-overlay-light)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px'
        }}>
          <Icon name="bike" color="muted" darkMode={darkMode} size={64} style={{ marginBottom: '16px' }} />
          <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>No bikes yet</h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>
            Add your first bike to start tracking your services.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setShowAddBikeModal(true);
              setAddBikeError('');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto' }}
          >
            <Icon name="upload" color="primary" darkMode={darkMode} size={16} />
            Add a bike
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px'
        }}>
          {bikes.map(bike => (
            <div
              key={bike.id}
              onClick={() => navigate(`/bike/${bike.id}`)}
              style={{
                background: 'var(--bg-overlay-light)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '16px',
                padding: '24px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(160, 200, 20, 0.4)';
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Bike Image or Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.2) 0%, rgba(160, 200, 20, 0.1) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '40px',
                marginBottom: '16px',
                position: 'relative'
              }}>
                {bike.profile_image_url ? (
                  <img 
                    src={bike.profile_image_url} 
                    alt={`${bike.brand} ${bike.model}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
                  />
                ) : (
                  <Icon name="bike" color="primary" darkMode={darkMode} size={40} />
                )}
                {/* Service status indicator */}
                {(() => {
                  const statusColor = getServiceStatusColor(bike);
                  if (statusColor) {
                    return (
                      <div style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-4px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: statusColor,
                        border: `2px solid ${darkMode ? '#1a1a1a' : '#ffffff'}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Bike Info */}
              <h3 style={{ 
                color: '#ffffff', 
                margin: '0 0 8px 0', 
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {bike.brand} {bike.model}
              </h3>
              
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px', 
                marginBottom: '12px' 
              }}>
                {bike.year && (
                  <span style={{
                    padding: '4px 10px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Icon name="calendar" color="secondary" darkMode={darkMode} size={12} />
                    {bike.year}
                  </span>
                )}
                {bike.color && (
                  <span style={{
                    padding: '4px 10px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Icon name="palette" color="secondary" darkMode={darkMode} size={12} />
                    {bike.color}
                  </span>
                )}
              </div>

              {/* Frame Number */}
              <div style={{
                padding: '10px 12px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)',
                letterSpacing: '0.5px'
              }}>
                Frame: {bike.vin}
              </div>

              {/* Service Count */}
              {bike.service_count > 0 && (
                <div style={{
                  marginTop: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  color: '#4A8C18'
                }}>
                  <Icon name="service" color="brand" darkMode={darkMode} size={14} />
                  <span>{bike.service_count} service entries</span>
                </div>
              )}

              {/* Upcoming Bookings */}
              {(() => {
                const bikeBookings = getBikeBookings(bike.id);
                if (bikeBookings.length > 0) {
                  return (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}>
                      <div style={{ color: '#60a5fa', fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon name="calendar" color="info" darkMode={darkMode} size={14} />
                        Service booked
                      </div>
                      {bikeBookings.map(booking => {
                        const bookingDate = new Date(booking.booking_date);
                        const isToday = booking.booking_date === new Date().toISOString().split('T')[0];
                        return (
                          <div key={booking.id} style={{ color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                            {isToday ? 'Today' : bookingDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })} {booking.start_time?.substring(0, 5)} @ {booking.company?.name || 'Service shop'}
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Stolen status indicator */}
              {bike.is_stolen && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: '#f87171'
                }}>
                  <Icon name="warning" color="error" darkMode={darkMode} size={14} />
                  <span>STOLEN</span>
                </div>
              )}

              {/* Stolen/Found button */}
              <div style={{ marginTop: '12px' }}>
                {bike.is_stolen ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleReportFound(bike.id); }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      background: 'rgba(16, 185, 129, 0.15)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '6px',
                      color: '#4A8C18',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Icon name="success" color="success" darkMode={darkMode} size={12} />
                    Mark as found
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowStolenModal(bike); }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      borderRadius: '6px',
                      color: '#f87171',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Icon name="warning" color="error" darkMode={darkMode} size={12} />
                    Report as stolen
                  </button>
                )}
              </div>

              {/* Arrow indicator */}
              <div style={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                color: 'rgba(255,255,255,0.3)',
                fontSize: '18px'
              }}>
                →
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stolen Confirmation Modal */}
      {showStolenModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowStolenModal(null)}
        >
          <div 
            style={{
              background: darkMode ? '#1a1a3e' : '#ffffff',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '450px',
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Icon name="warning" color="error" darkMode={darkMode} size={48} style={{ marginBottom: '12px' }} />
              <h2 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '20px', 
                color: darkMode ? '#ffffff' : '#111827'
              }}>
                Report bike as stolen
              </h2>
              <p style={{ 
                margin: 0, 
                color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280',
                fontSize: '14px'
              }}>
                <strong>{showStolenModal.brand} {showStolenModal.model}</strong>
              </p>
            </div>

            <div style={{
              padding: '16px',
              background: darkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px',
              marginBottom: '20px',
              fontSize: '14px',
              color: darkMode ? '#fbbf24' : '#d97706',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <Icon name="warning" color="warning" darkMode={darkMode} size={18} style={{ marginTop: '2px', flexShrink: 0 }} />
              <span><strong>Important:</strong> If you report your bike as stolen, every partner shop will see a warning when this bike is brought in. This helps recover stolen bikes.</span>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowStolenModal(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReportStolen(showStolenModal.id)}
                style={{
                  padding: '10px 20px',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  justifyContent: 'center'
                }}
              >
                <Icon name="warning" color="primary" darkMode={false} size={16} />
                Report as stolen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Bike Modal */}
      {showAddBikeModal && (
        <Modal
          onClose={() => {
            setShowAddBikeModal(false);
            setBikeForm({ vin: '', brand: '', model: '', year: new Date().getFullYear(), description: '' });
            setAddBikeError('');
          }}
          title="Add New Bike"
        >
          <form onSubmit={handleAddBike} style={{ width: '100%', maxWidth: '100%' }}>
            {addBikeError && <div className="error" style={{ marginBottom: '16px' }}>{addBikeError}</div>}
            <div className="form-group" style={{ width: '100%' }}>
              <label>Frame Number *</label>
              <input
                type="text"
                value={bikeForm.vin}
                onChange={(e) => setBikeForm({ ...bikeForm, vin: e.target.value })}
                required
                maxLength={50}
                placeholder="Enter frame number"
                disabled={addingBike}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ width: '100%' }}>
              <label>Brand *</label>
              <input
                type="text"
                value={bikeForm.brand}
                onChange={(e) => setBikeForm({ ...bikeForm, brand: e.target.value })}
                required
                placeholder="e.g., Trek, Specialized"
                disabled={addingBike}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ width: '100%' }}>
              <label>Model *</label>
              <input
                type="text"
                value={bikeForm.model}
                onChange={(e) => setBikeForm({ ...bikeForm, model: e.target.value })}
                required
                placeholder="e.g., Domane SL 5"
                disabled={addingBike}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ width: '100%' }}>
              <label>Year *</label>
              <input
                type="number"
                value={bikeForm.year}
                onChange={(e) => setBikeForm({ ...bikeForm, year: parseInt(e.target.value) || new Date().getFullYear() })}
                required
                min="1900"
                max="2030"
                disabled={addingBike}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="form-group" style={{ width: '100%' }}>
              <label>Description (Optional)</label>
              <textarea
                value={bikeForm.description}
                onChange={(e) => setBikeForm({ ...bikeForm, description: e.target.value })}
                placeholder="Any additional information about your bike..."
                rows={3}
                disabled={addingBike}
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
              <button type="submit" className="btn btn-primary" disabled={addingBike} style={{ flex: '1 1 auto', minWidth: '120px' }}>
                {addingBike ? 'Adding...' : 'Add Bike'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddBikeModal(false);
                  setBikeForm({ vin: '', brand: '', model: '', year: new Date().getFullYear(), description: '' });
                  setAddBikeError('');
                }}
                className="btn btn-secondary"
                disabled={addingBike}
                style={{ flex: '1 1 auto', minWidth: '120px' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Service Reminder Modal for bikes >12 months since last service */}
      {showServiceReminderModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => handleDismissServiceReminder(showServiceReminderModal.id)}
        >
          <div 
            style={{
              background: darkMode ? '#1a1a3e' : '#ffffff',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '450px',
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Icon name="service" color="primary" darkMode={darkMode} size={48} style={{ marginBottom: '12px' }} />
              <h2 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '20px', 
                color: darkMode ? '#ffffff' : '#111827'
              }}>
                Is everything OK with your bike?
              </h2>
              <p style={{ 
                margin: 0, 
                color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280',
                fontSize: '14px'
              }}>
                <strong>{showServiceReminderModal.brand} {showServiceReminderModal.model}</strong>
              </p>
              <p style={{ 
                margin: '8px 0 0 0', 
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af',
                fontSize: '13px'
              }}>
                It's been over a year since your last service. Everything working well?
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => handleDismissServiceReminder(showServiceReminderModal.id)}
                className="btn btn-secondary"
                style={{ flex: 1, minWidth: '120px' }}
              >
                Everything's OK
              </button>
              <Link
                to="/book"
                className="btn btn-primary"
                style={{ 
                  flex: 1, 
                  minWidth: '120px',
                  textDecoration: 'none',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
                onClick={() => handleDismissServiceReminder(showServiceReminderModal.id)}
              >
                Book Service
              </Link>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default MyBikesPage;

